import React, { useEffect, useRef, useState } from 'react';
import { Spinner, OrderBar, BackPage } from 'components';
import { DeliveryDetailsPageContent } from 'services/content.js';
import styles from './style.module.css';
import * as Types from 'helpers/types';
import * as API from 'services/api';
import { useSelector, useDispatch } from 'react-redux';
import infoCircle from 'assets/info-circle-black.svg';
import questionCircle from 'assets/question-circle.svg';
import plusImage from 'assets/plus-green.svg';
import { useHistory } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import useODSCode from 'hooks/useODSCode';
import { useInDeliveryRange, useInDeliveryRangeDirect } from 'hooks/useInDeliveryRange';
import useDirectDeliveryRange from 'hooks/useDirectDeliveryRange';

const DeliveryDetails = () => {
  let query = useQuery();
  let history = useHistory();
  const dispatch = useDispatch();
  const addressState = useSelector((state) => state.addresses);
  const setAddress = useSelector((state) => state.paymentJourney.address);
  const basket = useSelector((state) => state.paymentJourney.basket);
  const startedJourney = useSelector((state) => state.paymentJourney.started);
  const deliveryType = useSelector((state) => state.paymentJourney.delivery_type);
  const numContraceptives = useSelector((state) => state.paymentJourney.numContraceptives);
  const selectedPharmacyDetails = useSelector((state) => state.paymentJourney.selectedPharmacyDetails);
  const dspExclusion = useSelector((state) => state.paymentJourney.dspExclusion);
  const gpRegisteredInRange = useSelector((state) => state.paymentJourney.gpRegisteredInRange);
  const selectedPharmacyUser =
    selectedPharmacyDetails?.delivery_available && selectedPharmacyDetails?.delivery_enabled && gpRegisteredInRange;
  const [loadingInRange, setLoadingInRange] = useState(false);
  const [displayInRangeOverlay, setDisplayInRangeOverlay] = useState(false);
  const getFromODSCode = useODSCode();
  const getInRange = useInDeliveryRange();
  const getInRangeDirect = useInDeliveryRangeDirect();
  const getDirectDeliveryRange = useDirectDeliveryRange();
  const registeredAddress = addressState.addresses.find((address) => {
    return address?.name === 'GP Registered';
  });
  let nextPage;

  if (deliveryType === 'pharmacyDelivery') {
    nextPage = '/pharmacy/dashboard/basket';
  } else {
    nextPage = basket.length !== numContraceptives ? '/pharmacy/dashboard/exemption' : '/pharmacy/dashboard/basket';
  }

  const accountView = query.get('view') === 'account' ? true : false;

  const didNotLoadRef = useRef(true);

  const hasNoAddress = (setAddress && Object.keys(setAddress).length === 0) || typeof setAddress === 'undefined';

  const [disableNext, setDisableNext] = useState(true);

  useEffect(() => {
    if (!startedJourney && !accountView) history.push('/pharmacy/dashboard');
    async function wrappedGetAddress() {
      try {
        if (didNotLoadRef.current) {
          dispatch({ type: Types.ADDRESSES_LOADING });
          const addressRes = await API.getAddress();
          dispatch({ type: Types.ADDRESSES_SUCCESS, payload: addressRes });

          if (hasNoAddress) {
            const registeredAddress = addressRes.find((address) => {
              return address?.name === 'GP Registered';
            });
            if (registeredAddress) {
              if (deliveryType === 'pharmacyDelivery') {
                if (selectedPharmacyUser) {
                  dispatch({
                    type: Types.ADDRESS_SET,
                    payload: { ...registeredAddress, ods_code: selectedPharmacyDetails.ods_code },
                  });
                  const pharmaciesInRange = await getDirectDeliveryRange(selectedPharmacyDetails.ods_code);
                  dispatch({ type: Types.PHARMACIES_IN_RANGE_SET, payload: pharmaciesInRange });
                } else {
                  const previousPharmacyODS = setAddress.ods_code;
                  dispatch({
                    type: Types.ADDRESS_SET,
                    payload: { ...registeredAddress, ods_code: previousPharmacyODS },
                  });
                  const pharmaciesInRange = await getDirectDeliveryRange(previousPharmacyODS);
                  dispatch({ type: Types.PHARMACIES_IN_RANGE_SET, payload: pharmaciesInRange });
                }
              } else {
                dispatch({ type: Types.ADDRESS_SET, payload: registeredAddress });
              }
              setDisableNext(false);
            } else setDisableNext(true);
          } else if (!setAddress?.id) {
            const loadedAddress = addressRes.find((address) => {
              return address?.name === setAddress?.name && address?.postcode === setAddress?.postcode;
            });
            if (deliveryType === 'pharmacyDelivery') {
              if (selectedPharmacyUser) {
                dispatch({
                  type: Types.ADDRESS_SET,
                  payload: { ...loadedAddress, ods_code: selectedPharmacyDetails.ods_code },
                });
                const pharmaciesInRange = await getDirectDeliveryRange(selectedPharmacyDetails.ods_code);
                dispatch({ type: Types.PHARMACIES_IN_RANGE_SET, payload: pharmaciesInRange });
              } else {
                const previousPharmacyODS = setAddress.ods_code;
                dispatch({
                  type: Types.ADDRESS_SET,
                  payload: { ...loadedAddress, ods_code: previousPharmacyODS },
                });
                const pharmaciesInRange = await getDirectDeliveryRange(previousPharmacyODS);
                dispatch({ type: Types.PHARMACIES_IN_RANGE_SET, payload: pharmaciesInRange });
              }
            } else {
              dispatch({ type: Types.ADDRESS_SET, payload: loadedAddress });
            }
            setDisableNext(false);
          } else setDisableNext(false);
          didNotLoadRef.current = false;
        }
      } catch (e) {
        dispatch({ type: Types.ADDRESSES_ERROR, payload: e });
      }
    }

    wrappedGetAddress();
  }, [didNotLoadRef, dispatch, startedJourney, history, accountView, hasNoAddress]);

  const changeToCollection = async () => {
    const pharmacyDetails = await getFromODSCode(selectedPharmacyDetails.ods_code);
    const addressPayload = {
      ...pharmacyDetails.data.address,
      ods_code: selectedPharmacyDetails.ods_code,
      name: pharmacyDetails.data.name,
    };
    dispatch({ type: Types.CLEAR_PAYMENT_AND_EXEMPTION });
    dispatch({ type: Types.DELIVERY_METHOD_SET, payload: 'collectFromPharmacy' });
    dispatch({ type: Types.ADDRESS_SET, payload: addressPayload });
    history.push('/pharmacy/dashboard/basket');
  };

  const setGPRegisteredAddress = () => {
    if (selectedPharmacyUser) {
      dispatch({
        type: Types.ADDRESS_SET,
        payload: { ...registeredAddress, ods_code: selectedPharmacyDetails.ods_code },
      });
    } else {
      const previousPharmacyODS = setAddress.ods_code;
      dispatch({
        type: Types.ADDRESS_SET,
        payload: { ...registeredAddress, ods_code: previousPharmacyODS },
      });
    }
    history.push('/pharmacy/dashboard/basket');
  };

  const updateAddress = async (e, address) => {
    e.stopPropagation();
    if (deliveryType === 'pharmacyDelivery') {
      if (selectedPharmacyUser) {
        dispatch({
          type: Types.ADDRESS_SET,
          payload: { ...address, ods_code: selectedPharmacyDetails.ods_code },
        });
        const pharmaciesInRange = await getDirectDeliveryRange(selectedPharmacyDetails.ods_code);
        dispatch({ type: Types.PHARMACIES_IN_RANGE_SET, payload: pharmaciesInRange });
      } else {
        const previousPharmacyODS = setAddress.ods_code;
        dispatch({
          type: Types.ADDRESS_SET,
          payload: { ...address, ods_code: previousPharmacyODS },
        });
        const pharmaciesInRange = await getDirectDeliveryRange(previousPharmacyODS);
        dispatch({ type: Types.PHARMACIES_IN_RANGE_SET, payload: pharmaciesInRange });
      }
    } else {
      if (!selectedPharmacyUser && deliveryType === 'homeDelivery') {
        const pharmaciesInRange = await getDirectDeliveryRange(address.postcode);
        dispatch({ type: Types.PHARMACIES_IN_RANGE_SET, payload: pharmaciesInRange });
      }
      dispatch({ type: Types.ADDRESS_SET, payload: address });
    }
    setDisableNext(false);
  };

  const displayAddress = (address, showName = false) => {
    return (
      <div
        tabIndex="0"
        onKeyPress={(event) => (event.key === 'Enter' ? updateAddress(event, address) : null)}
        onClick={(e) => updateAddress(e, address)}
        className={`${styles.box_container} ${styles.clickable} drop-shadow`}
        key={address.id}>
        <div className={`${styles.box_container__grid}`}>
          <div className={styles.image_container}>
            <div className={`${styles.dot} ${setAddress?.id === address.id && styles.dot__active}`} />
          </div>

          <div>
            {showName && <h2 className={styles.addressName}>{address.name}</h2>}
            <p className={styles.addressText}>{address.address_line1}</p>
            <p className={styles.addressText}>{address.address_line2}</p>
            <p className={styles.addressText}>{address.address_line3}</p>
            <p className={styles.addressText}>{address.address_line4}</p>
            <p className={styles.addressText}>{address.postcode}</p>
          </div>
        </div>
      </div>
    );
  };

  const displayAddressAccount = (address, notGP = false) => {
    return (
      <div className={`${styles.box_container} ${notGP && styles.row} drop-shadow`} key={address.id}>
        <div>
          {notGP && <h2 className={styles.addressName}>{address.name}</h2>}
          <p className={styles.addressText}>{address.address_line1}</p>
          <p className={styles.addressText}>{address.address_line2}</p>
          <p className={styles.addressText}>{address.address_line3}</p>
          <p className={styles.addressText}>{address.address_line4}</p>
          <p className={styles.addressText}>{address.postcode}</p>
        </div>
        {notGP && (
          <div className={styles.editContainer}>
            <button
              className={styles.link_button}
              href="#"
              onClick={() => {
                dispatch({ type: Types.EDIT_ADDRESS_SET, payload: address });
                history.push(`/pharmacy/dashboard/account/address/edit?view=account`);
              }}>
              Edit
            </button>
          </div>
        )}
      </div>
    );
  };

  const displayAddresses = () => {
    if (addressState.loading) return <Spinner text={DeliveryDetailsPageContent.addressLoadingText} />;
    if (addressState.error) return <div>Error</div>;

    const registeredAddress = addressState.addresses.find((address) => {
      return address.name === 'GP Registered';
    });

    const additionalAddresses = addressState.addresses.filter((address) => {
      return address.name !== 'GP Registered';
    });

    return (
      <>
        {registeredAddress !== undefined ? (
          <>
            <h2 className={styles.singleLine}>
              Your registered address
              <img
                src={questionCircle}
                onClick={() => history.push(`/pharmacy/tooltip/1`)}
                className={styles.questionCircle}
                alt="registered address info"
              />
            </h2>
            {accountView ? displayAddressAccount(registeredAddress) : displayAddress(registeredAddress)}
          </>
        ) : null}

        <h2>Your addresses</h2>
        {additionalAddresses.length > 0 ? (
          <>
            {additionalAddresses.map((address) =>
              accountView ? displayAddressAccount(address, true) : displayAddress(address, true)
            )}
          </>
        ) : null}
        <div
          tabIndex="0"
          onKeyPress={(event) =>
            event.key === 'Enter'
              ? accountView
                ? history.push(`/pharmacy/dashboard/account/address/postcode?view=account`)
                : history.push(`/pharmacy/dashboard/account/address/postcode`)
              : null
          }
          onClick={() =>
            accountView
              ? history.push(`/pharmacy/dashboard/account/address/postcode?view=account`)
              : history.push(`/pharmacy/dashboard/account/address/postcode`)
          }
          className={`${styles.box_container}  ${styles.clickable} drop-shadow`}>
          <div className={styles.box_container__grid}>
            <div className={`${styles.image_container} ${styles.plus_container}`}>
              <img src={plusImage} className={styles.plus} alt="add new address" />
            </div>

            <p className={styles.addCardText}>{DeliveryDetailsPageContent.addAddressText}</p>
          </div>
        </div>
      </>
    );
  };

  const progressPage = async () => {
    if (!accountView && deliveryType === 'pharmacyDelivery' && setAddress.name !== 'GP Registered') {
      try {
        setLoadingInRange(true);
        let inRange;
        if (selectedPharmacyDetails?.ods_code) {
          inRange = await getInRange(setAddress.ods_code, setAddress.postcode);
        } else {
          inRange = await getInRangeDirect(setAddress.ods_code, setAddress.postcode);
        }
        if (!inRange.data) {
          setDisplayInRangeOverlay(true);
          return;
        }
      } finally {
        setLoadingInRange(false);
      }
    }
    if (!accountView && !selectedPharmacyUser && deliveryType === 'homeDelivery' && dspExclusion) {
      history.push('/pharmacy/dashboard/delivery-options');
    } else {
      history.push(nextPage);
    }
  };

  return (
    <>
      {!accountView && displayInRangeOverlay && (
        <div className={styles.overlay}>
          <div className={styles.overlayContainer}>
            <h2>{DeliveryDetailsPageContent.pharmacyDeliveryOverlayHeader}</h2>
            <p>{DeliveryDetailsPageContent.pharmacyDeliveryOverlayText}</p>
            {selectedPharmacyUser ? (
              <button className="coop-btn coop-btn--primary" onClick={() => changeToCollection()}>
                {DeliveryDetailsPageContent.selectedPharmacyDeliveryOverlayOption1}
              </button>
            ) : (
              <button
                className="coop-btn coop-btn--primary"
                onClick={() => history.push('/pharmacy/dashboard/delivery-options')}>
                {DeliveryDetailsPageContent.pharmacyDeliveryOverlayOption1}
              </button>
            )}
            <button className="coop-btn coop-btn--primary" onClick={() => setGPRegisteredAddress()}>
              {DeliveryDetailsPageContent.pharmacyDeliveryOverlayOption2 + registeredAddress.postcode}
            </button>
          </div>
        </div>
      )}
      {!accountView && <OrderBar currentProgress={0} />}
      <BackPage
        onBackClick={() => (accountView ? history.goBack() : history.push('/pharmacy/dashboard/delivery-options'))}>
        <h1 className={styles.header}>{DeliveryDetailsPageContent.headingText}</h1>
        <p className={styles.intro_text}> {DeliveryDetailsPageContent.introText}</p>
        <div className={styles.list_items}>{displayAddresses()}</div>
        {!accountView && (
          <button
            className={`coop-btn coop-btn--primary ${styles.button}`}
            onClick={() => progressPage()}
            id="continuelink"
            disabled={disableNext || loadingInRange}>
            {DeliveryDetailsPageContent.continueButtonText}
          </button>
        )}
      </BackPage>
    </>
  );
};

export default DeliveryDetails;
